import {
    Box,
    Button,
    ButtonGroup,
    capitalize,
    Chip,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {AbwesenheitInfo} from "../../api";
import FilterBenutzerSelect from "../benutzer/FilterBenutzerSelect";
import {Field, Formik} from "formik";
import {useApproveMultipleAbwesenheiten} from "../../client/hooks";
import {useQueryClient} from "react-query";
import DataTable from "../data/DataTable";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import StartEndFilter from "../StartEndFilter";
import useCustomFilters from "../data/useCustomFilters";

export type AbwesenheitenFilter = {
    search?: string;
    mitarbeiterId?: string;
    filtered?: string;
    startDate?: string;
    endDate?: string;
};

interface AbwesenheitenProps {
    abwesenheitIds: string[];
    setAbwesenheitIds: (value: string[]) => void;
}

export type Props = Omit<DataRequest<AbwesenheitenFilter>, "filter"> &
    Partial<Pick<DataRequest<AbwesenheitenFilter>, "filter">>;

const tableName: string = "abwesenheiten"

export default function AbwesenheitDataTable({...input}: Props) {
    const request = useDataRequest<AbwesenheitenFilter>({filter: {}, ...input});
    const [abwesenheitIds, setAbwesenheitIds] = useState<string[]>([]);

    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                abwesenheitIds={abwesenheitIds}
                setAbwesenheitIds={setAbwesenheitIds}
                filteredStateFilter={filteredState}
                setFilteredState={setFilteredState}
                {...request}
            />
            <AbwesenheitsResults
                abwesenheitIds={abwesenheitIds}
                setAbwesenheitIds={setAbwesenheitIds}
                filteredStateFilter={filteredState}
                setFilteredState={setFilteredState}
                {...request}
            />
        </Stack>
    );
}

function FilterComp({
                        abwesenheitIds,
                        setAbwesenheitIds,
                        filteredStateFilter,
                        setFilteredState,
                        ...request
                    }: AbwesenheitenProps & {
    filteredStateFilter: string;
    setFilteredState: (value: string) => void;
} & DataRequestState<AbwesenheitenFilter>) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AbwesenheitenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            mitarbeiterId: undefined,
            startDate: "",
            endDate: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const approve = useApproveMultipleAbwesenheiten();
    const queryClient = useQueryClient();
    const {t: t1} = useT();
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            mitarbeiterId: currentCustomFilter.mitarbeiterId,
            startDate: currentCustomFilter.startDate,
            endDate: currentCustomFilter.endDate,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    async function approveAbwesenheiten(abwesenheitsIds: Array<string>) {
        await approve(abwesenheitsIds);
        await queryClient.invalidateQueries([tableName]);
        navigate(`/abwesenheit`);
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <StartEndFilter startDatum={currentCustomFilter.startDate}
                                    endeDatum={currentCustomFilter.endDate}
                                    setCustomField={setCustomField}
                    />
                    <Box sx={{ml: 2}}></Box>
                    <Formik
                        initialValues={{mitarbeiterId: currentCustomFilter.mitarbeiterId}}
                        onSubmit={() => {
                        }}
                    >
                        <Field
                            component={FilterBenutzerSelect}
                            name={"mitarbeiterId"}
                            label={"Mitarbeiter"}
                            setBenutzer={(val: any) => setCustomField("mitarbeiterId", val)}
                        ></Field>
                    </Formik>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-abwesenheit");
                        }}
                    >
                        {capitalize(t1("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <StartEndFilter startDatum={currentCustomFilter.startDate}
                                    endeDatum={currentCustomFilter.endDate}
                                    setCustomField={setCustomField}
                    />
                    <Box sx={{ml: 2}}></Box>
                    <Formik
                        initialValues={{mitarbeiterId: currentCustomFilter.mitarbeiterId}}
                        onSubmit={() => {
                        }}
                    >
                        <Field
                            component={FilterBenutzerSelect}
                            name={"mitarbeiterId"}
                            label={"Mitarbeiter"}
                            setBenutzer={(val: any) => setCustomField("mitarbeiterId", val)}
                        ></Field>
                    </Formik>
                    <ButtonGroup sx={{position: "absolute", top: 0, right: 0}}>
                        <Button
                            variant="contained"
                            color="success"
                            sx={{marginRight: 2}}
                            onClick={() => approveAbwesenheiten(abwesenheitIds)}
                        >
                            {capitalize(t1("approve"))}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                navigate("/neu-abwesenheit");
                            }}
                        >
                            {capitalize(t1("create-new"))}
                        </Button>
                    </ButtonGroup>
                </>
            )}
        </Box>
    );
}

function AbwesenheitsResults({
                                 abwesenheitIds,
                                 setAbwesenheitIds,
                                 filteredStateFilter,
                                 setFilteredState,
                                 ...request
                             }: AbwesenheitenProps & {
    filteredStateFilter: string;
    setFilteredState: (value: string) => void;
} & DataRequestState<AbwesenheitenFilter>) {
    return (
        <Results
            abwesenheitIds={abwesenheitIds}
            setAbwesenheitIds={setAbwesenheitIds}
            request={request}
            setFilteredState={setFilteredState}
        />
    );
}

function Results({
                     setAbwesenheitIds,
                     request,
                     setFilteredState,
                 }: AbwesenheitenProps & {
    request: DataRequestState<AbwesenheitenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAbwesenheiten: DataLoader<AbwesenheitenFilter, AbwesenheitInfo> =
        useCallback(
            async (params) =>
                (await getApi()).abwesenheiten.sucheAbwesenheiten({...params}),
            [getApi]
        );

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAbwesenheiten}
            name={tableName}
            // @ts-ignore
            checkboxSelection={true}
            disableRowSelectionOnClick={true}
            onRowSelectionModelChange={(newRowSelectionModel: any) => {
                setAbwesenheitIds(newRowSelectionModel);
            }}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open("/abwesenheit/" + row.id, "_blank");
                } else {
                    navigate("/abwesenheit/" + row.id);
                }
            }}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<AbwesenheitInfo>> {
    const {t: translation} = useT("abwesenheit");
    return useMemo(
        () => [
            {
                field: "name",
                headerName: capitalize(translation("abwesenheit-attribute-name")),
                width: 200,
            },
            {
                field: "type",
                headerName: capitalize(translation("abwesenheit-attribute-type")),
            },
            {
                field: "start",
                headerName: capitalize(translation("abwesenheit-attribute-start")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "ende",
                headerName: capitalize(translation("abwesenheit-attribute-end")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "tage",
                headerName: capitalize(translation("abwesenheit-attribute-days")),
                align: "right",
                headerAlign: "right",
                type: "number",
            },
            {
                field: "erstelltAm",
                headerName: capitalize(translation("abwesenheit-attribute-created-at")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "bemerkung",
                headerName: capitalize(translation("abwesenheit-attribute-remark")),
            },
            {
                field: "status",
                headerName: capitalize(translation("abwesenheit-attribute-status")),
                flex: 1,
                renderCell: (params) => {
                    switch (params.value) {
                        case "OFFEN":
                            return (
                                <Chip
                                    icon={<SpellcheckIcon/>}
                                    title={"Offen"}
                                    color={"secondary"}
                                    label={"Offen"}
                                ></Chip>
                            );
                        case "ANGENOMMEN":
                            return (
                                <Chip
                                    icon={<PlaylistAddCheckCircleIcon/>}
                                    title={"Freigegeben"}
                                    color={"success"}
                                    label={"Freigegeben"}
                                ></Chip>
                            );
                    }
                },
            },
        ],
        [translation]
    );
}
